import React, { useMemo, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import BusinessCard from "@/components/BusinessCard";
import { buildVCard, downloadVCard, shareVCard } from "@/lib/vcard";

export default function BusinessCardModal({ open, onOpenChange, name, email, role, profile = {}, title = "My digital business card", contentClassName = "" }) {
  const qrRef = useRef(null);
  const vcard = useMemo(() => buildVCard({ name, email, profile }), [name, email, profile]);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  const saveContact = () => {
    downloadVCard(vcard, `${(name || "contact").replace(/\s+/g, "_")}.vcf`);
    toast.success("Contact card downloaded");
  };

  const share = async () => {
    const r = await shareVCard({ name, email, profile });
    if (r === "downloaded") toast.success("Sharing not supported here — card downloaded instead");
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${(name || "qr").replace(/\s+/g, "_")}_qr.png`;
    a.click();
    toast.success("QR code saved");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-md p-0 overflow-hidden gap-0 ${contentClassName}`} data-testid="business-card-modal">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription className="text-xs">Share these contact details — scan the QR, share, or download the vCard.</DialogDescription>
        </DialogHeader>
        <div className="px-5 pb-5 space-y-5 max-h-[80vh] overflow-y-auto">
          <BusinessCard name={name} email={email} role={role} profile={profile} />

          <div className="flex flex-col items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 p-5" ref={qrRef}>
            <QRCodeCanvas value={vcard} size={200} level="M" includeMargin fgColor="#1B4332" data-testid="business-card-qr" />
            <p className="text-xs text-gray-500 text-center mt-1">Scan with a phone camera to save these contact details.</p>
          </div>

          <div className="space-y-2">
            {canShare && (
              <Button onClick={share} className="w-full bg-[#1B4332] hover:bg-[#143728]" data-testid="share-card-btn">
                <Icons.Share2 className="w-4 h-4 mr-1.5" /> Share card
              </Button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={saveContact} variant={canShare ? "outline" : "default"}
                className={canShare ? "" : "bg-[#1B4332] hover:bg-[#143728]"} data-testid="download-vcard-btn">
                <Icons.Download className="w-4 h-4 mr-1.5" /> Save contact
              </Button>
              <Button onClick={downloadQR} variant="outline" data-testid="download-qr-btn">
                <Icons.QrCode className="w-4 h-4 mr-1.5" /> Save QR image
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
